import { useState, useId, useRef, useEffect } from "react";

// --- Illustrative PTC data: as film temperature rises, relative power falls.
// Capped at 50°C — the ecoHeatwave product's real maximum set-point.
const DATA = [
  { t: 20, p: 100 },
  { t: 25, p: 95 },
  { t: 30, p: 88 },
  { t: 35, p: 78 },
  { t: 40, p: 65 },
  { t: 45, p: 55 },
  { t: 50, p: 45 },
];

const STEPS = [
  {
    n: "01",
    title: "Cold start",
    body:
      "When a room is below its target temperature, the heating film's electrical resistance is at its lowest, drawing maximum power and bringing surfaces up to comfort quickly.",
  },
  {
    n: "02",
    title: "Self-regulating response",
    body:
      "As the film warms, the conductive carbon layer's resistance rises in direct proportion to its temperature, automatically reducing the power it draws from the circuit.",
  },
  {
    n: "03",
    title: "Steady-state comfort",
    body:
      "At the room's comfort set-point, the film stabilises and draws only the energy required to maintain even radiant warmth — no cycling, no overshoot, no overheating.",
  },
];

const BENEFITS = [
  {
    title: "Inherently safe",
    body: "The film cannot overheat — rising temperature itself cuts the power.",
  },
  {
    title: "Energy aware",
    body: "Power demand falls as the room approaches its target, not after.",
  },
  {
    title: "Even surface temperature",
    body: "Any localised hotspot self-cools, delivering uniform radiant warmth.",
  },
  {
    title: "No thermostat chatter",
    body: "Smooth modulation replaces the on-off cycling of resistive systems.",
  },
  {
    title: "Longer service life",
    body: "Gentler thermal stress on the film means a longer, lower-maintenance life.",
  },
];

// --- Graph geometry (viewBox units — scales responsively via SVG).
const VB = { w: 760, h: 360 };
const PAD = { top: 28, right: 28, bottom: 56, left: 64 };
const PLOT = {
  w: VB.w - PAD.left - PAD.right,
  h: VB.h - PAD.top - PAD.bottom,
};
const TEMP_MIN = 20;
const TEMP_MAX = 50;
const POWER_MIN = 0;
const POWER_MAX = 100;

const xFor = (t) =>
  PAD.left + ((t - TEMP_MIN) / (TEMP_MAX - TEMP_MIN)) * PLOT.w;
const yFor = (p) =>
  PAD.top + (1 - (p - POWER_MIN) / (POWER_MAX - POWER_MIN)) * PLOT.h;

const X_TICKS = [20, 25, 30, 35, 40, 45, 50];
const Y_TICKS = [0, 20, 40, 60, 80, 100];

export default function PTCTechnology() {
  return (
    <section
      id="ptc"
      data-testid="ptc-section"
      className="mt-[20mm] md:mt-[26mm]"
    >
      <PTCIntro />
      <HowItWorks />
      <PTCGraph />
      <PTCBenefits />
      <PTCClosing />
    </section>
  );
}

function PTCIntro() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
      <div className="lg:col-span-5">
        <div className="overline mb-6">PTC Technology</div>
        <h3
          data-testid="ptc-headline"
          className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.08] text-ecoh-ink"
        >
          Heating that regulates itself.
        </h3>
      </div>
      <div className="lg:col-span-7 space-y-5 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
        <p>
          ecoHeatwave uses a Positive Temperature Coefficient (PTC) conductive
          carbon layer. Unlike fixed-output heating elements, its electrical
          resistance rises in direct proportion to its own temperature.
        </p>
        <p>
          As the film warms, the current it draws naturally falls. The system
          modulates its own power without a mechanical thermostat intervening,
          and reaches a stable comfort temperature by material physics alone.
        </p>
        <p>
          The practical effect is a heating system that cannot overheat, uses
          less electricity as rooms approach their set-point, and delivers an
          even surface temperature across every installed panel.
        </p>
        <p className="text-ecoh-ink font-medium">
          Comfort by design. Efficiency by physics.
        </p>
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <div
      data-testid="ptc-how-it-works"
      className="mt-16 md:mt-20 border-t border-ecoh-ink/10 pt-14 md:pt-16"
    >
      <div className="overline mb-10">How PTC Technology Works</div>
      <ol className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
        {STEPS.map((s) => (
          <li key={s.n} data-testid={`ptc-step-${s.n}`} className="flex flex-col">
            <div className="text-[11px] tracking-[0.22em] uppercase text-ecoh-accent font-medium">
              {s.n}
            </div>
            <h4 className="mt-4 font-display text-xl md:text-2xl font-light tracking-tight leading-snug text-ecoh-ink">
              {s.title}
            </h4>
            <p className="mt-4 text-ecoh-muted text-[15px] md:text-base font-light leading-relaxed">
              {s.body}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

// --- Chart styling and behaviour constants.
const COLOR = {
  accent: "#F65900",
  ink: "#1C1B1A",
  label: "#5C5A56",
  grid: "#E5E5E0",
  halo: "#F9F8F6",
  white: "#FFFFFF",
};
const AXIS = {
  tick: 4,
  opacity: 0.35,
  yLabelGap: 10,
  xLabelGap: 18,
  xTitleInset: 10,
  yTitleX: 16,
  labelSize: 11,
  titleSize: 10,
  titleSpacing: 2,
};
const SERIES = { width: 2.25, fillOpacity: 0.08 };
const POINT = { r: 3.5, rActive: 5.5, halo: 2, hitR: 18, growMs: 160 };
const TOOLTIP = { w: 230, h: 34, gap: 14, radius: 6, fontSize: 12, guideOpacity: 0.12 };
const ANIM = {
  drawMs: 1600,
  ease: "cubic-bezier(0.33, 1, 0.68, 1)", // easeOutCubic
  pointFadeMs: 300,
  pointLead: 0.9,
  viewThreshold: 0.33,
};

const PLOT_BOTTOM = PAD.top + PLOT.h;
const PLOT_RIGHT = PAD.left + PLOT.w;
const AXIS_LINE = { stroke: COLOR.ink, strokeOpacity: AXIS.opacity };
const LABEL_TEXT = { fill: COLOR.label, fontFamily: "inherit" };
const TITLE_TEXT = { ...LABEL_TEXT, fontSize: AXIS.titleSize, letterSpacing: AXIS.titleSpacing };

// --- Path builders (data → SVG `d` strings).
function buildLinePath(data) {
  return data
    .map((d, i) => `${i === 0 ? "M" : "L"} ${xFor(d.t)} ${yFor(d.p)}`)
    .join(" ");
}

function buildAreaPath(data) {
  const first = xFor(data[0].t);
  const last = xFor(data[data.length - 1].t);
  const line = data.map((d) => `L ${xFor(d.t)} ${yFor(d.p)}`).join(" ");
  return `M ${first} ${yFor(0)} ${line} L ${last} ${yFor(0)} Z`;
}

function GraphGridlines() {
  return (
    <>
      {Y_TICKS.map((p) => (
        <line key={`gy-${p}`} x1={PAD.left} x2={PLOT_RIGHT} y1={yFor(p)} y2={yFor(p)} stroke={COLOR.grid} strokeWidth="1" />
      ))}
      <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PLOT_BOTTOM} strokeWidth="1" {...AXIS_LINE} />
      <line x1={PAD.left} y1={PLOT_BOTTOM} x2={PLOT_RIGHT} y2={PLOT_BOTTOM} strokeWidth="1" {...AXIS_LINE} />
    </>
  );
}

function YAxis() {
  return Y_TICKS.map((p) => (
    <g key={`ty-${p}`}>
      <line x1={PAD.left - AXIS.tick} x2={PAD.left} y1={yFor(p)} y2={yFor(p)} {...AXIS_LINE} />
      <text
        x={PAD.left - AXIS.yLabelGap}
        y={yFor(p)}
        textAnchor="end"
        dominantBaseline="central"
        fontSize={AXIS.labelSize}
        {...LABEL_TEXT}
      >
        {p}
      </text>
    </g>
  ));
}

function XAxis() {
  return X_TICKS.map((t) => (
    <g key={`tx-${t}`}>
      <line x1={xFor(t)} x2={xFor(t)} y1={PLOT_BOTTOM} y2={PLOT_BOTTOM + AXIS.tick} {...AXIS_LINE} />
      <text x={xFor(t)} y={PLOT_BOTTOM + AXIS.xLabelGap} textAnchor="middle" fontSize={AXIS.labelSize} {...LABEL_TEXT}>
        {t}
      </text>
    </g>
  ));
}

function AxisTitles() {
  return (
    <>
      <text x={PAD.left + PLOT.w / 2} y={VB.h - AXIS.xTitleInset} textAnchor="middle" {...TITLE_TEXT}>
        HEATING FILM TEMPERATURE (°C)
      </text>
      <text
        x={-(PAD.top + PLOT.h / 2)}
        y={AXIS.yTitleX}
        transform="rotate(-90)"
        textAnchor="middle"
        {...TITLE_TEXT}
      >
        RELATIVE ELECTRICAL POWER (%)
      </text>
    </>
  );
}

// Calls `onEnter` once when the element is sufficiently visible; returns cleanup.
function observeOnce(el, onEnter) {
  const obs = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return;
      onEnter();
      obs.disconnect();
    },
    { threshold: ANIM.viewThreshold }
  );
  obs.observe(el);
  return () => obs.disconnect();
}

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Draws once on first view; instant under reduced motion.
function useDrawOnce() {
  const ref = useRef(null);
  const [animate] = useState(() => !prefersReducedMotion());
  const [drawn, setDrawn] = useState(!animate);
  useEffect(() => {
    if (drawn) return undefined;
    return observeOnce(ref.current, () => setDrawn(true));
  }, [drawn]);
  return { ref, drawn, animate };
}

function GraphSeries({ data, drawn, animate }) {
  const t = (prop) => (animate ? `${prop} ${ANIM.drawMs}ms ${ANIM.ease}` : "none");
  return (
    <>
      <path
        d={buildAreaPath(data)}
        fill={COLOR.accent}
        fillOpacity={SERIES.fillOpacity}
        style={{ opacity: drawn ? 1 : 0, transition: t("opacity") }}
      />
      <path
        data-testid="ptc-graph-line"
        d={buildLinePath(data)}
        pathLength="1"
        fill="none"
        stroke={COLOR.accent}
        strokeWidth={SERIES.width}
        strokeLinejoin="round"
        strokeLinecap="round"
        strokeDasharray="1"
        style={{ strokeDashoffset: drawn ? 0 : 1, transition: t("stroke-dashoffset") }}
      />
    </>
  );
}

// Time at which an easeOutCubic line reaches fraction `f` of its length.
const easeOutCubicInverse = (f) => 1 - Math.cbrt(1 - f);

function GraphPoint({ d, index, count, isActive, setActive, drawn, animate }) {
  const cx = xFor(d.t);
  const cy = yFor(d.p);
  const reach = easeOutCubicInverse(index / (count - 1));
  const delay = animate ? reach * ANIM.drawMs * ANIM.pointLead : 0;
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={isActive ? POINT.rActive : POINT.r}
        fill={COLOR.accent}
        stroke={COLOR.halo}
        strokeWidth={POINT.halo}
        style={{
          opacity: drawn ? 1 : 0,
          transition: `r ${POINT.growMs}ms ease-out, opacity ${ANIM.pointFadeMs}ms ease-out ${delay}ms`,
        }}
      />
      <circle
        cx={cx}
        cy={cy}
        r={POINT.hitR}
        fill="transparent"
        onMouseEnter={() => setActive(index)}
        onFocus={() => setActive(index)}
        onClick={() => setActive(index)}
        tabIndex={0}
        role="button"
        aria-label={`Temperature ${d.t}°C, relative power ${d.p}%`}
        style={{ cursor: "pointer", outline: "none" }}
      />
    </g>
  );
}

function GraphPoints({ data, active, ...rest }) {
  return data.map((d, i) => (
    <GraphPoint key={d.t} d={d} index={i} count={data.length} isActive={active === i} {...rest} />
  ));
}

function tooltipBox(cx, cy) {
  const x = Math.min(Math.max(cx - TOOLTIP.w / 2, PAD.left), PLOT_RIGHT - TOOLTIP.w);
  const above = cy - TOOLTIP.h - TOOLTIP.gap;
  const y = above > PAD.top ? above : cy + TOOLTIP.gap;
  return { x, y };
}

function GraphTooltip({ point }) {
  if (!point) return null;
  const cx = xFor(point.t);
  const box = tooltipBox(cx, yFor(point.p));
  return (
    <g pointerEvents="none">
      <line
        x1={cx}
        x2={cx}
        y1={PAD.top}
        y2={PLOT_BOTTOM}
        stroke={COLOR.ink}
        strokeOpacity={TOOLTIP.guideOpacity}
        strokeDasharray="3 3"
      />
      <rect x={box.x} y={box.y} width={TOOLTIP.w} height={TOOLTIP.h} rx={TOOLTIP.radius} ry={TOOLTIP.radius} fill={COLOR.ink} />
      <text
        x={box.x + TOOLTIP.w / 2}
        y={box.y + TOOLTIP.h / 2 + 1}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={TOOLTIP.fontSize}
        fill={COLOR.white}
        fontFamily="inherit"
      >
        {`Temperature: ${point.t}°C · Power: ${point.p}%`}
      </text>
    </g>
  );
}

function GraphHeader() {
  return (
    <div className="flex items-baseline justify-between gap-4 mb-6 md:mb-8">
      <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-ecoh-ink">
        Illustrative PTC Power Response
      </h4>
      <div className="text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-ecoh-muted">
        Figure 01
      </div>
    </div>
  );
}

function GraphSvg({ drawn, animate }) {
  const [active, setActive] = useState(null);
  const titleId = `${useId()}-title`;
  return (
    <svg
      viewBox={`0 0 ${VB.w} ${VB.h}`}
      preserveAspectRatio="xMidYMid meet"
      className="block w-full h-auto select-none"
      role="img"
      aria-labelledby={titleId}
      onMouseLeave={() => setActive(null)}
    >
      <title id={titleId}>
        Line graph showing relative electrical power decreasing from 100% at
        20°C to 45% at 50°C as the heating film temperature rises.
      </title>
      <GraphGridlines />
      <YAxis />
      <XAxis />
      <AxisTitles />
      <GraphSeries data={DATA} drawn={drawn} animate={animate} />
      <GraphPoints data={DATA} active={active} setActive={setActive} drawn={drawn} animate={animate} />
      <GraphTooltip point={active != null ? DATA[active] : null} />
    </svg>
  );
}

function PTCGraph() {
  const { ref, drawn, animate } = useDrawOnce();
  return (
    <div
      ref={ref}
      data-testid="ptc-graph"
      data-drawn={drawn}
      className="mt-16 md:mt-20 rounded-3xl bg-ecoh-bg ring-1 ring-ecoh-ink/10 p-6 md:p-10 lg:p-12 max-w-5xl mx-auto"
    >
      <GraphHeader />
      <GraphSvg drawn={drawn} animate={animate} />
      <p
        data-testid="ptc-disclaimer"
        className="mt-6 text-xs md:text-[13px] leading-relaxed text-ecoh-muted max-w-3xl"
      >
        Actual behaviour will vary with installation, ambient conditions,
        control strategy, and building fabric.
      </p>
    </div>
  );
}

function PTCBenefits() {
  return (
    <div className="mt-16 md:mt-20 max-w-5xl mx-auto">
      <div className="overline mb-10">Benefits</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-20 gap-y-10">
        {BENEFITS.map((b, i) => (
          <div
            key={b.title}
            data-testid={`ptc-benefit-${i}`}
            className="border-t border-ecoh-ink/10 pt-5"
          >
            <h4 className="font-display text-lg md:text-xl font-normal tracking-tight text-ecoh-ink">
              {b.title}
            </h4>
            <p className="mt-2 text-ecoh-muted text-[15px] md:text-base font-light leading-relaxed">
              {b.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PTCClosing() {
  return (
    <div
      data-testid="ptc-closing"
      className="mt-16 md:mt-20 max-w-3xl"
    >
      <h4 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
        Designed for Modern, Energy-Conscious Homes.
      </h4>
      <p className="mt-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
        PTC self-regulation is one of the quiet reasons ecoHeatwave fits so
        naturally into contemporary low-carbon homes — pairing invisibly with
        solar PV, battery storage and smart controls to deliver comfort that
        uses only the energy a room actually needs.
      </p>
    </div>
  );
}
